import type { GameState } from '../state';
import { sub, gte, type Decimal } from '../math/num';
import { getFrame, frameLevelCost } from '../data/frames';
import { MAX_EFFORT_DURATION_MS, MAX_EFFORT_CD_MS, FULL_SEND_CD_MS, fullSendBurst, segmentStart } from './combat';
import type { EmitFn } from './events';

// ---- Frame leveling (Scrap sink) -------------------------------------------

export function nextFrameLevelCost(state: GameState): Decimal {
  return frameLevelCost(getFrame(state.frame.defId), state.frame.level);
}

export function canLevelFrame(state: GameState): boolean {
  return gte(state.wallet.scrap, nextFrameLevelCost(state));
}

export function levelFrame(state: GameState): boolean {
  const cost = nextFrameLevelCost(state);
  if (!gte(state.wallet.scrap, cost)) return false;
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  state.frame.level += 1;
  return true;
}

export function levelFrameMax(state: GameState): number {
  let bought = 0;
  while (bought < 10_000 && levelFrame(state)) bought += 1;
  return bought;
}

// ---- Wave navigation (farming) ----------------------------------------------

export type WaveNav = 'first' | 'back' | 'forward' | 'frontier';

/** Where a nav step would land (used by the UI to enable/disable buttons).
 *  You may only move forward through waves already beaten; `highestWave` is the
 *  frontier (the highest wave reached, i.e. the lowest not yet beaten). */
export function navWaveTarget(state: GameState, dir: WaveNav): number {
  const c = state.combat;
  if (dir === 'first') return 1;
  if (dir === 'back') return Math.max(1, c.wave - 1);
  if (dir === 'forward') return c.wave < c.highestWave ? c.wave + 1 : c.wave;
  return c.highestWave;
}

/** Jump the run to another beaten wave (or the frontier). Clears the current
 *  horde; the next tick spawns the new one. Blocked mid-duel and mid-pause. */
export function navWave(state: GameState, dir: WaveNav): boolean {
  const c = state.combat;
  if (c.overlordId || c.pauseKind) return false;
  const target = navWaveTarget(state, dir);
  if (target === c.wave) return false;
  c.wave = target;
  c.checkpoint = segmentStart(target);
  c.enemies = [];
  return true;
}

// ---- Active play -----------------------------------------------------------

export function fullSend(state: GameState, emit?: EmitFn): number {
  return fullSendBurst(state, emit);
}

export interface FullSendStatus {
  ready: boolean;
  remainingMs: number;
  /** Recharge fill, 0..1: 1 when ready, climbing back up while on cooldown. */
  progress: number;
}

export function fullSendStatus(state: GameState): FullSendStatus {
  const remainingMs = Math.max(0, state.combat.fullSendCdUntil - state.elapsedMs);
  const progress = remainingMs <= 0 ? 1 : 1 - remainingMs / FULL_SEND_CD_MS;
  return { ready: remainingMs <= 0, remainingMs, progress };
}

export interface MaxEffortStatus {
  active: boolean;
  ready: boolean;
  remainingMs: number;
  /** Fill, 0..1: drains over the active burst, then climbs back as it recharges. */
  progress: number;
}

export function maxEffortStatus(state: GameState): MaxEffortStatus {
  const active = state.combat.maxEffortUntil > state.elapsedMs;
  const ready = state.elapsedMs >= state.combat.maxEffortCdUntil;
  const remainingMs = active
    ? state.combat.maxEffortUntil - state.elapsedMs
    : Math.max(0, state.combat.maxEffortCdUntil - state.elapsedMs);
  const progress = active
    ? remainingMs / MAX_EFFORT_DURATION_MS
    : ready ? 1 : 1 - remainingMs / MAX_EFFORT_CD_MS;
  return { active, ready, remainingMs, progress };
}

export function triggerMaxEffort(state: GameState): boolean {
  if (state.elapsedMs < state.combat.maxEffortCdUntil) return false;
  state.combat.maxEffortUntil = state.elapsedMs + MAX_EFFORT_DURATION_MS;
  state.combat.maxEffortCdUntil = state.elapsedMs + MAX_EFFORT_CD_MS;
  return true;
}
